/**
 * Pure helpers for the "Offboard user" form (epic #288). The form component and the page both
 * lean on these rules, so they live outside React: empty state, the settings-driven deletion
 * date, validation, the user option label and the API payload.
 */
import { isIsoDate } from "../DateField";

/** Fallback when the SCO form settings do not carry a deletion default. */
export const DEFAULT_DELETION_DAYS = 90;

/** The empty offboarding request the form starts from. */
export const EMPTY_OFFBOARDING = {
  userPublicId: "",
  endDate: "",
  deletionDate: "",
  computerDisposition: "",
  convertToSharedMailbox: false,
  sharedMailboxAccessUserPublicIds: [],
  forwardMail: false,
  forwardToUserPublicId: "",
  autoReplyText: "",
  comment: "",
};

/**
 * Adds days to a yyyy-MM-dd date without timezone drift: the date is parsed as UTC midnight and
 * shifted in UTC, so a local timezone can never move the result to the neighbouring day.
 *
 * @param {string} isoDate - Date in yyyy-MM-dd form
 * @param {number} days - Days to add; negative shifts backwards
 * @returns {string} The shifted date in yyyy-MM-dd form, or "" if the input is unusable
 */
const shiftIsoDate = (isoDate, days) => {
  // A half-typed end date ("2026-11-3") yields no default rather than a guessed one.
  if (!isIsoDate(isoDate)) return "";

  const parsed = new Date(`${isoDate}T00:00:00Z`);
  parsed.setUTCDate(parsed.getUTCDate() + days);
  return parsed.toISOString().slice(0, 10);
};

/**
 * The deletion date the form pre-fills from the end date: the customer's configured number of
 * days after the account's last day.
 *
 * @param {string} endDate - The user's end date in yyyy-MM-dd form
 * @param {number} [days] - Days between the end date and the deletion date
 * @returns {string} The deletion date in yyyy-MM-dd form, or "" when there is no end date
 */
export function defaultDeletionDate(endDate, days = DEFAULT_DELETION_DAYS) {
  const offset = Number.isFinite(days) ? days : DEFAULT_DELETION_DAYS;
  return shiftIsoDate(endDate, offset);
}

/**
 * Validates an offboarding request.
 *
 * @param {object} value - The form value, shaped like EMPTY_OFFBOARDING
 * @param {string} todayIso - Today's date in yyyy-MM-dd form (local time)
 * @returns {string|null} The first problem to show the orderer, or null when it may be sent
 */
export function validateOffboarding(value, todayIso) {
  if (!value?.userPublicId) return "Choose the user to offboard";

  // An end date in the past is allowed: offboardings are often reported after the fact.
  if (!value.endDate) return "Choose the end date";
  if (!isIsoDate(value.endDate)) return "Enter the end date as yyyy-mm-dd";

  if (!value.deletionDate) return "Choose the deletion date";
  if (!isIsoDate(value.deletionDate)) return "Enter the deletion date as yyyy-mm-dd";
  if (value.deletionDate <= value.endDate) return "The deletion date must be after the end date";
  if (value.deletionDate <= todayIso) return "The deletion date must be in the future";

  if (value.convertToSharedMailbox && (value.sharedMailboxAccessUserPublicIds || []).length === 0) {
    return "Choose who should have access to the shared mailbox";
  }

  if (value.forwardMail && !value.forwardToUserPublicId) {
    return "Choose who should receive the forwarded mail";
  }

  return null;
}

/**
 * The label a user is picked by: enough to tell two people with the same name apart, and the
 * manager so the orderer can double-check they picked the right colleague.
 *
 * @param {object} user - A tenant user
 * @returns {string} "Name · principal name · Manager: name"
 */
export function userOptionLabel(user) {
  const principalName = user?.userPrincipalName || user?.email || "-";
  const displayName = user?.displayName || principalName;
  const manager = user?.managerDisplayName || "-";

  return `${displayName} · ${principalName} · Manager: ${manager}`;
}

const orNull = (value) => (value === "" || value === undefined ? null : value);

/**
 * Builds the API payload. Fields belonging to a switch that is off are cleared, so a wish the
 * orderer changed their mind about never reaches the case mail.
 *
 * @param {object} value - The form value, shaped like EMPTY_OFFBOARDING
 * @param {{ source: string, tenantPublicId: string }} context - Where the request comes from
 * @returns {object} The body for submitOffboarding
 */
export function buildOffboardingPayload(value, { source, tenantPublicId }) {
  return {
    source,
    tenantPublicId,
    userPublicId: value.userPublicId,
    endDate: value.endDate,
    deletionDate: value.deletionDate,
    computerDisposition: orNull(value.computerDisposition),
    convertToSharedMailbox: !!value.convertToSharedMailbox,
    sharedMailboxAccessUserPublicIds: value.convertToSharedMailbox
      ? [...(value.sharedMailboxAccessUserPublicIds || [])]
      : [],
    forwardMail: !!value.forwardMail,
    forwardToUserPublicId: value.forwardMail ? orNull(value.forwardToUserPublicId) : null,
    autoReplyText: orNull(value.autoReplyText),
    comment: orNull(value.comment),
  };
}
